import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Users, Wallet, TrendingDown, TrendingUp, Scale } from "lucide-react";

const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 6 }, (_, i) => String(currentYear - 3 + i));

export default function CommunityDashboardPage() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(String(currentYear));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/community/dashboard`, { params: { year: parseInt(year) } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [year]);

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;
  if (!data) return <div className="text-gray-400">Error cargando datos</div>;

  const cards = [
    { icon: Users, label: "Propietarios activos", value: data.total_owners, color: "text-[#002FA7]" },
    { icon: Wallet, label: "Cuotas cobradas", value: formatCurrency(data.fees_paid), color: "text-emerald-600" },
    { icon: TrendingDown, label: "Cuotas pendientes", value: formatCurrency(data.fees_pending), color: "text-amber-600" },
    { icon: TrendingUp, label: "Total gastos", value: formatCurrency(data.total_expenses), color: "text-red-600" },
    { icon: Scale, label: "Balance", value: formatCurrency(data.balance), color: data.balance >= 0 ? "text-emerald-600" : "text-red-600" },
  ];

  return (
    <div data-testid="community-dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Comunidad de Propietarios</h2>
          <p className="text-sm text-gray-500 mt-1">Resumen contable {data.year}</p>
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28 rounded-sm" data-testid="community-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <c.icon className={`w-4 h-4 ${c.color}`} strokeWidth={1.5} />
              <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-gray-500">{c.label}</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly breakdown */}
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="monthly-table">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-bold tracking-tight text-gray-900">Desglose mensual</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Mes</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Ingresos</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Gastos</TableHead>
                <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.monthly.map(m => (
                <TableRow key={m.month} className="hover:bg-gray-50">
                  <TableCell className="text-sm text-gray-900 py-2 px-4">{MONTHS_ES[m.month-1]}</TableCell>
                  <TableCell className="text-sm text-emerald-600 py-2 px-4 text-right">{formatCurrency(m.income)}</TableCell>
                  <TableCell className="text-sm text-red-600 py-2 px-4 text-right">{formatCurrency(m.expenses)}</TableCell>
                  <TableCell className={`text-sm font-medium py-2 px-4 text-right ${m.balance >= 0 ? "text-gray-900" : "text-red-600"}`}>{formatCurrency(m.balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Expenses by category */}
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="expenses-by-cat">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-bold tracking-tight text-gray-900">Gastos por categoria</h3>
          </div>
          <div className="p-4 space-y-3">
            {Object.keys(data.expenses_by_category).length === 0 ? (
              <p className="text-sm text-gray-400">Sin gastos registrados</p>
            ) : Object.entries(data.expenses_by_category).sort((a,b) => b[1] - a[1]).map(([cat, amount]) => (
              <div key={cat} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{cat}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(amount)}</span>
              </div>
            ))}
            {Object.keys(data.expenses_by_category).length > 0 && (
              <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-sm font-bold text-gray-900">{formatCurrency(data.total_expenses)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
